<template>
    <Panel :title="$t('Number Management')" :help-text="$t('DESC_NUMBER_MANAGEMENT')">
        <div id="number_table_paging">
            <div id="number_table_records">
                <div>{{ $t('Total number of records') }}: {{ totalRows }}</div>

                <CicButton id="display_all_numbers" @click="$emit('change')">
                    {{ $t('DESC_NUMBER_DISPLAY_' + (displayGroupNumbers ? 1 : 0)) }}
                </CicButton>
            </div>

            <CicPageControl :page="page" :total-rows="totalRows" @change="changePage" />
        </div>
        <div id="number_table_controls">
            <div id="number_group_controls">
                <template v-if="$store.hasRight('ASSIGN_TO_NUMBER_GROUPS')">
                    <CicButton
                        id="assign_number_group"
                        :enabled="
                            selected.length > 0 &&
                            !selected.some((key) => getNumberFromKey(key).group) &&
                            !selected.some((key) => getNumberFromKey(key).groupId)
                        "
                        @click="openGroupSelect"
                    >
                        {{ $t('Assign to Number Group') }}
                    </CicButton>

                    <CicButton
                        id="unassign_number_group"
                        :enabled="selected.length > 0 && !selected.some((key) => !getNumberFromKey(key).groupId)"
                        @click="unassignFromGroups"
                    >
                        {{ $t('Unassign from Number Group') }}
                    </CicButton>
                </template>

                <CicButton
                    v-if="$store.hasRight('EDIT_INBOUND_NUMBERS')"
                    id="remove_origination_group"
                    :enabled="
                        selected.length > 0 && !selected.some((key) => key.substring(key.indexOf('|') + 1) === '0')
                    "
                    @click="removeOriginationGroup"
                >
                    {{ $t('Remove CLI Group') }}
                </CicButton>
            </div>

            <div id="number_io_controls">
                <CicButton
                    v-if="$store.hasRight('EDIT_ROUTE_PLANS')"
                    id="number_import"
                    @click="showImportOptions = true"
                >
                    {{ $t('Import') }}
                </CicButton>
                <CicButton id="number_export" @click="exportAccessPoints">{{ $t('Export') }}</CicButton>
            </div>
        </div>

        <div style="overflow-x: auto">
            <table id="tblNumberManagement" class="cic-table" style="text-align: left">
                <thead>
                    <tr>
                        <th
                            :colspan="
                                5 +
                                ($store.hasRight('ASSIGN_TO_NUMBER_GROUPS') ? 1 : 0) +
                                ($store.hasRight('VIEW_SUBORGANISATIONS') ? 1 : 0) +
                                (displayGroupNumbers ? 1 : 0) +
                                ($store.hasRight('VIEW_ROUTING_SCHEDULES') ? 1 : 0)
                            "
                            class="end-cell"
                        >
                            {{ $t('Access Point') }}
                        </th>
                        <th :colspan="3 + (canRecord ? 1 : 0) + (canVoicemail ? 1 : 0)">
                            {{ $t('Primary Route Plan') }}
                        </th>
                        <th colspan="2" class="start-cell">{{ $t('DR Route Plan') }}</th>
                    </tr>
                    <tr>
                        <th v-if="$store.hasRight('ASSIGN_TO_NUMBER_GROUPS')" class="th-checkbox">
                            <CicCheckbox
                                :value="
                                    selected.length > 0 &&
                                    numbers.filter((number) => number.orgId === $store.user.orgId).length ===
                                        selected.length
                                "
                                @input="selectAll"
                            />
                        </th>
                        <th>{{ $t('Access Point') }}</th>
                        <th>{{ $t('Access Type') }}</th>
                        <th>{{ $t('CLI_GROUP') }}</th>
                        <th v-if="$store.hasRight('VIEW_SUBORGANISATIONS')">{{ $t('Organisation') }}</th>
                        <th>{{ $t('Description') }}</th>
                        <th v-if="displayGroupNumbers">{{ $t('NUMBER_GROUP') }}</th>
                        <th v-if="$store.hasRight('VIEW_ROUTING_SCHEDULES')" class="th-image">
                            {{ $t('ROUTING_SCHEDULE') }}
                        </th>
                        <th class="th-checkbox end-cell">{{ $t('DR active?') }}</th>
                        <th>{{ $t('ROUTE_TYPE') }}</th>
                        <th>{{ $t('Route Plan') }}</th>
                        <th v-if="canRecord" class="th-checkbox">{{ $t('Record?') }}</th>
                        <th v-if="canVoicemail" class="th-checkbox">{{ $t('Voicemail?') }}</th>
                        <th class="th-checkbox">{{ $t('Whisper?') }}</th>
                        <th class="start-cell">{{ $t('ROUTE_TYPE') }}</th>
                        <th>{{ $t('Route Plan') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="number in numbers"
                        :class="{ inactive: !number.planId && (!number.drActive || !number.drPlanId) }"
                    >
                        <td v-if="$store.hasRight('ASSIGN_TO_NUMBER_GROUPS')" class="td-checkbox">
                            <CicCheckbox
                                :enabled="number.orgId === $store.user.orgId"
                                :value="selected.includes(number.key)"
                                @input="handleSelected(number.key)"
                            />
                        </td>
                        <td>
                            <span class="EditAccessLink" @click="openForm(number)">
                                {{ number.name }}
                            </span>
                        </td>
                        <td>
                            {{ $t(number.group ? 'NUMBER_GROUP' : `ACCESS_TYPE_${number.type}`) }}
                        </td>
                        <td>
                            {{
                                [0, 1, 2, 4].includes(number.type)
                                    ? number.originationGroupId
                                        ? number.originationName
                                        : $t('Default')
                                    : $t('N/A')
                            }}
                        </td>
                        <td v-if="$store.hasRight('VIEW_SUBORGANISATIONS')">{{ number.orgName }}</td>
                        <td>{{ number.description }}</td>
                        <template v-if="displayGroupNumbers">
                            <td v-if="$store.hasRight('EDIT_NUMBER_GROUPS')">
                                <span v-if="number.groupId" class="EditGroupLink" @click="openGroup(number)">
                                    {{ number.groupName }}
                                </span>
                            </td>
                            <td v-else>{{ number.groupId ? number.groupName : '' }}</td>
                        </template>
                        <td v-if="$store.hasRight('VIEW_ROUTING_SCHEDULES')">
                            <span
                                :class="{ alt: number.scheduleId > 0 }"
                                :title="$t('Edit Schedule')"
                                class="edit-button"
                                @click="openSchedule(number)"
                            ></span>
                        </td>
                        <td class="end-cell">
                            <CicCheckbox
                                :value="number.drActive"
                                :enabled="number.drPlanId > 0 && $store.hasRight('EDIT_INBOUND_NUMBERS')"
                                @input="updateDR(number)"
                            />
                        </td>
                        <td v-if="number.planType > 0">{{ routeTypes[number.planType] }}</td>
                        <td v-else>
                            <b>{{ $t('UNASSIGNED') }}</b>
                        </td>
                        <td>{{ number.planName }}</td>
                        <td v-if="canRecord" style="text-align: center">
                            <CicCheckbox
                                v-if="[1, 2].includes(number.planType) && number.canRecord"
                                :value="number.record"
                                :enabled="$store.hasRight('EDIT_ROUTE_PLANS')"
                                @input="updatePlan(number.planId, 'recording', number.record ? 0 : 1)"
                            />
                            <span v-else>{{ $t('N/A') }}</span>
                        </td>
                        <td v-if="canVoicemail" style="text-align: center">
                            <CicCheckbox
                                v-if="[1, 2].includes(number.planType) && number.canVoicemail"
                                :value="number.voicemail"
                                :enabled="$store.hasRight('EDIT_ROUTE_PLANS')"
                                @input="
                                    number.voicemail
                                        ? updatePlan(number.planId, 'voicemailprofiles')
                                        : openVoicemailSelect(number)
                                "
                            />
                            <span v-else>{{ $t('N/A') }}</span>
                        </td>
                        <td style="text-align: center">
                            <CicCheckbox
                                v-if="[1, 2].includes(number.planType)"
                                :value="number.whisper"
                                :enabled="$store.hasRight('EDIT_ROUTE_PLANS')"
                                @input="
                                    number.whisper
                                        ? updatePlan(number.planId, 'whisperprompts')
                                        : openWhisperSelect(number)
                                "
                            />
                            <span v-else>{{ $t('N/A') }}</span>
                        </td>
                        <td v-if="number.drPlanType > 0" class="start-cell">{{ routeTypes[number.drPlanType] }}</td>
                        <td v-else class="start-cell">
                            <b>{{ $t('UNASSIGNED') }}</b>
                        </td>
                        <td>{{ number.drPlanName }}</td>
                    </tr>
                </tbody>
            </table>
        </div>

        <EditAccessPoint v-if="item" :access-point="item" @blur="closePopup" @change="reload" />

        <ExportAccessPoints
            v-if="showExportOptions && !$store.showLoadingOverlay"
            @input="exportAccessPoints"
            @blur="closePopup"
        />

        <ImportAccessPoints v-if="showImportOptions" @blur="closePopup" @change="reload" />

        <RoutingSchedules v-if="schedule" :access-point="schedule" @blur="closePopup" @change="loadTable" />

        <AccessPointGroups v-if="group" :access-point="group" @blur="closePopup" @change="loadTable" />

        <AccessPointGroupSelect
            :org-id="groupOrgId"
            :voice-only="!selected.some((key) => getNumberFromKey(key).type !== 1)"
            @blur="closePopup"
            @input="assignToGroup"
        />

        <WhisperSelect
            :org-id="whisperNumber ? whisperNumber.orgId : 0"
            @blur="closePopup"
            @input="(listId, promptId) => updatePlan(whisperNumber.planId, 'whisperprompts', promptId, listId)"
        />

        <VoicemailSelect
            :org-id="voicemailNumber ? voicemailNumber.orgId : 0"
            @blur="closePopup"
            @input="(val) => updatePlan(voicemailNumber.planId, 'voicemailprofiles', val)"
        />
    </Panel>
</template>

<script>
    import Panel from '@/components/container/Panel';
    import CicPageControl from '@/components/CicPageControl';
    import CicButton from '@/components/CicButton';
    import CicCheckbox from '@/components/inputs/CicCheckbox';
    import AccessPointGroups from '@/feature/inbound/accessPointGroups/AccessPointGroups';
    import RoutingSchedules from '@/feature/inbound/routingSchedules/RoutingSchedules';
    import TableUtils from '@/mixins/TableUtils';
    import { load, save } from '@/utils/Http';
    import { getRouteTypeLabels } from '@/utils/RouteCollectionUtils';
    import EditAccessPoint from './editAccessPoint/EditAccessPoint';
    import AccessPointGroupSelect from './AccessPointGroupSelect';
    import ExportAccessPoints from './ExportAccessPoints';
    import ImportAccessPoints from './ImportAccessPoints';
    import VoicemailSelect from './VoicemailSelect';
    import WhisperSelect from './WhisperSelect';

    export default {
        name: 'AccessPointTable',
        components: {
            AccessPointGroupSelect,
            AccessPointGroups,
            CicCheckbox,
            CicButton,
            CicPageControl,
            EditAccessPoint,
            ExportAccessPoints,
            ImportAccessPoints,
            Panel,
            RoutingSchedules,
            VoicemailSelect,
            WhisperSelect,
        },
        mixins: [TableUtils],
        props: {
            displayGroupNumbers: {
                type: Boolean,
                default: false,
            },
            searches: {
                type: Array,
                required: true,
            },
        },
        emits: ['change', 'reload'],
        data() {
            return {
                canRecord: false,
                canVoicemail: false,
                group: null,
                groupOrgId: 0,
                item: null,
                numbers: [],
                page: 1,
                schedule: null,
                selected: [],
                showExportOptions: false,
                showImportOptions: false,
                totalRows: 0,
                voicemailNumber: null,
                whisperNumber: null,
            };
        },
        computed: {
            // does not translate properly if done in created()
            routeTypes() {
                return getRouteTypeLabels(false);
            },
        },
        watch: {
            displayGroupNumbers() {
                this.reload();
            },
            searches() {
                this.reload();
            },
        },
        created() {
            this.reload();
        },
        mounted() {
            document.getElementById('access_point_table').__vue__ = this;
        },
        methods: {
            assignToGroup(groupId) {
                const params = {
                    orgId: this.groupOrgId,
                    groupId,
                    accessIds: JSON.stringify(this.selected.map((key) => this.getNumberFromKey(key).accessId)),
                };
                save('accesspointgroup/assign', params, () => {
                    this.reload();
                });
            },
            closePopup() {
                this.group = null;
                this.item = null;
                this.schedule = null;
                this.showExportOptions = false;
                this.showImportOptions = false;
                this.groupOrgId = 0;
                this.whisperNumber = null;
                this.voicemailNumber = null;
            },
            exportAccessPoints(option) {
                if (!option && this.selected.length === 0) {
                    option = 'all';
                }
                if (option) {
                    const params = { type: option, groupNumbers: this.displayGroupNumbers ? 1 : 0 };
                    if (option === 'selected') {
                        params['selected'] = JSON.stringify(
                            this.selected.map((key) => {
                                const number = this.getNumberFromKey(key);
                                return { accessId: number.accessId, originationGroupId: number.originationGroupId };
                            }),
                        );
                    } else {
                        params['searches'] = JSON.stringify(this.searches);
                    }

                    this.$store.exportFile({ link: 'accesspoints/export', params });
                } else {
                    this.showExportOptions = true;
                }
            },
            getId(number) {
                return number.groupId ? number.groupId : number.accessId;
            },
            getNumberFromKey(key) {
                const accessId = parseInt(key.substring(0, key.indexOf('|')));
                const originationGroupId = parseInt(key.substring(key.indexOf('|') + 1));
                return this.numbers.find(
                    (number) => number.accessId === accessId && number.originationGroupId === originationGroupId,
                );
            },
            loadTable() {
                this.closePopup();
                const params = {
                    groupNumbers: this.displayGroupNumbers ? 1 : 0,
                    page: this.page,
                    searches: JSON.stringify(this.searches),
                };
                this.selected = [];
                load('accesspoints/search', params, (response) => {
                    this.numbers = response.numbers;
                    this.totalRows = response.totalRows;
                    this.canRecord = response.numbers.some((n) => n.canRecord);
                    this.canVoicemail = response.numbers.some((n) => n.canVoicemail);
                });
            },
            openForm(number) {
                this.item = {
                    accessId: this.getId(number),
                    orgId: number.orgId,
                    name: number.groupId ? number.groupName : number.name,
                    looping: number.looping,
                    originationGroupId: number.originationGroupId,
                    originationName: number.originationName,
                    type: number.groupId ? 0 : number.type,
                    description: number.description,
                    revision: number.revision,
                    planId: number.planId,
                    drPlanId: number.drPlanId,
                    drActive: number.drActive,
                };
            },
            openGroup(number) {
                this.group = {
                    orgId: number.orgId,
                    groupId: number.groupId,
                };
            },
            openGroupSelect() {
                this.closePopup();
                this.groupOrgId = this.$store.user.orgId;
            },
            openSchedule(number) {
                this.schedule = {
                    orgId: number.orgId,
                    accessId: this.getId(number),
                    name: number.name,
                    groupId: number.originationGroupId,
                    scheduleId: number.scheduleId,
                };
            },
            openVoicemailSelect(number) {
                this.closePopup();
                this.voicemailNumber = number;
            },
            openWhisperSelect(number) {
                this.closePopup();
                this.whisperNumber = number;
            },
            reload() {
                this.page = 1;
                this.loadTable();
            },
            removeOriginationGroup() {
                if (confirm(this.$t('DELETE_CLI_GROUP_CONFIG'))) {
                    const data = this.selected.map((key) => {
                        const number = this.getNumberFromKey(key);
                        const accessId = this.getId(number);
                        return { accessId, originationGroupId: number.originationGroupId };
                    });
                    save('accesspoints/removecligroup', { data: JSON.stringify(data) }, () => {
                        this.reload();
                        this.$emit('reload');
                    });
                }
            },
            selectAll(val) {
                this.selected = [];
                if (val) {
                    this.selected = this.numbers
                        .filter((number) => number.orgId === this.$store.user.orgId)
                        .map((number) => number.key);
                }
            },
            unassignFromGroups() {
                if (confirm(this.$t('REMOVE_FROM_GROUP_CONFIRM'))) {
                    const promises = [];
                    const orgParams = [];
                    this.selected.forEach((key) => {
                        const number = this.getNumberFromKey(key);
                        const found = orgParams.find((org) => org.groupId === number.groupId);
                        if (found) {
                            found.accessIds.push(number.accessId);
                        } else {
                            orgParams.push({
                                remove: 1,
                                orgId: number.orgId,
                                accessIds: [number.accessId],
                                groupId: number.groupId,
                            });
                        }
                    });
                    orgParams.forEach((params) => {
                        const promise = save('accesspointgroup/assign', {
                            ...params,
                            accessIds: JSON.stringify(params.accessIds),
                        });
                        promises.push(promise);
                    });
                    Promise.all(promises).then(() => {
                        this.selected = [];
                        this.loadTable();
                    });
                }
            },
            updateDR(number) {
                const accessId = this.getId(number);
                const params = {
                    accessId,
                    orgId: number.orgId,
                    originationGroupId: number.originationGroupId,
                    drActive: number.drActive ? 0 : 1,
                };
                save('accesspoint/updatedr', params, () => {
                    this.loadTable();
                    this.$emit('reload');
                });
            },
            updatePlan(planId, option, optionId = 0, groupId = 0) {
                const params = { planId, option, optionId, groupId };
                save('routeplan/updateoption', params, () => {
                    this.loadTable();
                });
            },
        },
    };
</script>
